import {useState, useContext} from "react";
import {bookingAPI} from "../common/api/bookingAPI";
import {API_BOOKING_REVIEW} from "../common/api/apiConstants";
import {createURLQueryFromParams} from "../common/api/apiUtil";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {useCurrencyConversion} from "./useCurrencyConversion";
import {CurrencyContext} from "../store/providers/CurrencyProvider";
import {INSURANCE_PURCHASE_CURRENCY, POINTS_EXTRA_PURCHASE_CURRENCY} from "../common/constant";

export const useBookingReview = () => {

    const {initialized, authenticated} = useKeycloakFunctions();
    const {getCurrencyConversion} = useCurrencyConversion();
    const [currencyState] = useContext(CurrencyContext);
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Internal functions
    const convertCurrency = (data, currency) => {
        const toConvert = [];

        if (currency !== INSURANCE_PURCHASE_CURRENCY) {
            for(const ins of data.insurances) {
                for (const opt of ins.options) {
                    toConvert.push({
                        label: opt?.sihot_response_code,
                        from_currency: INSURANCE_PURCHASE_CURRENCY,
                        to_currency: currency,
                        convert_amount: opt?.price
                    });
                }
            }
        }

        if ((currency !== POINTS_EXTRA_PURCHASE_CURRENCY) && (data?.points_extra_purchase?.amount > 0)) {
            toConvert.push({
                label: "points_extra_purchase",
                from_currency: POINTS_EXTRA_PURCHASE_CURRENCY,
                to_currency: currency,
                convert_amount: data?.points_extra_purchase?.costs
            });
        }

        return getCurrencyConversion(toConvert);
    };

    const attachConversionToResponse = (response, conversionResults, currency) => {
        if (currency !== INSURANCE_PURCHASE_CURRENCY) {
            for (const ins of response.insurances) {
                for (const opt of ins.options) {
                    const cr = conversionResults.find(r => r.label === opt.sihot_response_code);
                    opt.converted_price = cr?.result_amount;
                    opt.converted_currency = cr?.to_currency;
                }
            }
        }

        if ((currency !== POINTS_EXTRA_PURCHASE_CURRENCY) && (response?.points_extra_purchase?.amount > 0)) {
            response.points_extra_purchase.converted_costs = conversionResults.find(r => r.label === "points_extra_purchase")?.result_amount;
        }

        return response;
    };

    const getData = (params) => {
        if (initialized && authenticated) {
            setLoading(true);
            setError(null);

            return bookingAPI.get(API_BOOKING_REVIEW + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(async response => {
                    try {
                        if (response) {
                            const conversionResults = await convertCurrency(response, params.currency);
                            setLoading(false);
                            if (conversionResults && conversionResults.length) {
                                setData(attachConversionToResponse(response, conversionResults, params.currency));
                            } else {
                                setData(response);
                            }
                        }
                    } catch(err) {
                        // TODO expose a currencyConversion error to the component
                        // Throw a generic error for now
                        setLoading(false);
                        setError(err);
                        throw err;
                    }
                    return response;
                })
                .catch(err => {
                    setLoading(false);
                    setError(err);
                    throw err;
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };

    // Exposed functions
    const getBookingReviewData = (resort_id, apartment_short_code, date_from, date_to, high_season_points, low_season_points, locale, currency = null) => {
        const params = {
            resort_id,
            apartment_short_code,
            date_from,
            date_to,
            high_season_points: String(high_season_points),
            low_season_points: String(low_season_points),
            locale,
            currency: currency || currencyState?.currency // If specific currency is needed. Otherwise use currncy from state
        };

        return getData(params);
    };

    const getBookingReviewDataByReservationId = (reservation_id, locale, currency = null) => {
        const params = {
            reservation_id,
            locale,
            currency: currency || currencyState?.currency // If specific currency is needed. Otherwise use currncy from state
        };

        return getData(params);
    };

    return {
        getBookingReviewData,
        getBookingReviewDataByReservationId,
        bookingReviewData: data,
        loadingBookingReviewData: loading,
        errorBookingReviewData: error
    };
};
